import { useState, useEffect } from 'react'
import { FileText, Percent, Loader2, Clock, FileCheck, PlusCircle, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import TaxesService from '@/api/taxes.service'
import { useToast } from '@/hooks/use-toast'
import FormModal from '@/components/FormModal'
import { TaxTypeSelector } from '@/components/tax/TaxTypeSelector'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { TaxVerificationBadge } from '@/components/tax/TaxVerificationBadge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import countries from '@/components/Countries'

interface TaxType {
  id: number
  name: string
  code: string
  description: string
}

interface CustomerTaxInfo {
  id: number
  publicId: string
  taxIdentifier: string
  isVerified: boolean
  verificationDate?: string
  country: string
  notes?: string
  taxTypeId: number
  customerId: number
  taxType?: TaxType
}

interface CustomerTaxInfoProps {
  customerId?: number
  readonly?: boolean
  minimal?: boolean
  className?: string
  onTaxInfoChange?: (taxInfo: CustomerTaxInfo[]) => void
}

export function CustomerTaxInfo({ 
  customerId, 
  readonly = false, 
  minimal = false,
  className = '',
  onTaxInfoChange
}: CustomerTaxInfoProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [customerTaxInfo, setCustomerTaxInfo] = useState<CustomerTaxInfo[]>([])
  const [showAddTaxIdentifierModal, setShowAddTaxIdentifierModal] = useState(false)
  const [showVerifyTaxModal, setShowVerifyTaxModal] = useState(false)
  const [currentTaxToVerify, setCurrentTaxToVerify] = useState<CustomerTaxInfo | null>(null)
  
  // Form states
  const [newTaxIdentifier, setNewTaxIdentifier] = useState('')
  const [newTaxIdentifierType, setNewTaxIdentifierType] = useState<TaxType | null>(null)
  const [newTaxIdentifierCountry, setNewTaxIdentifierCountry] = useState('')
  const [newTaxIdentifierNotes, setNewTaxIdentifierNotes] = useState('')
  const [verificationMethod, setVerificationMethod] = useState<'automatic' | 'manual'>('automatic')
  const [verificationNotes, setVerificationNotes] = useState('')
  
  // Loading states
  const [isSavingIdentifier, setIsSavingIdentifier] = useState(false)
  const [isVerifyingTax, setIsVerifyingTax] = useState(false)
  const [isDeletingTax, setIsDeletingTax] = useState(false)
  
  const { toast } = useToast()

  // Load customer tax information
  useEffect(() => {
    const fetchCustomerTaxInfo = async () => {
      if (!customerId) return
      
      setIsLoading(true)
      try {
        const response = await TaxesService.getCustomerTaxInfos(customerId)
        console.log("response.data", response.data);
        if (response.data.result === 'success') {
          setCustomerTaxInfo(response.data.customerTaxInfos || [])
          if (onTaxInfoChange) {
            onTaxInfoChange(response.data.customerTaxInfos || [])
          }
        }
      } catch (error) {
        console.error('Failed to load customer tax information', error)
        toast({
          title: 'Error',
          description: 'Failed to load tax information',
          variant: 'destructive',
        })
      } finally {
        setIsLoading(false)
      }
    }
    
    if (customerId) {
      fetchCustomerTaxInfo()
    }
  }, [customerId, toast, onTaxInfoChange])

  const handleAddTaxIdentifier = async () => {
    if (!newTaxIdentifierType || !newTaxIdentifier || !newTaxIdentifierCountry || !customerId) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      })
      return
    }
    
    setIsSavingIdentifier(true)
    try {
      const payload = {
        taxIdentifier: newTaxIdentifier,
        taxTypeId: newTaxIdentifierType.id,
        country: newTaxIdentifierCountry,
        notes: newTaxIdentifierNotes,
        customerId: customerId,
        isVerified: false
      }
      
      const response = await TaxesService.createCustomerTaxInfo(payload)
      if (response.data.result === 'success') {
        toast({
          title: 'Tax identifier added',
          description: `${newTaxIdentifierType.name} information has been added successfully`,
        })
        
        // Add the new tax identifier to the list
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        
        // Reset form
        setNewTaxIdentifier('')
        setNewTaxIdentifierType(null)
        setNewTaxIdentifierNotes('')
        setShowAddTaxIdentifierModal(false)
      } else {
        toast({
          title: 'Failed to add tax identifier',
          description: response.data.message || 'An unexpected error occurred',
          variant: 'destructive',
        })
      }
    } catch (error) {
      console.error('Error adding tax identifier:', error)
      toast({
        title: 'Error',
        description: 'Failed to add tax identifier',
        variant: 'destructive',
      })
    } finally {
      setIsSavingIdentifier(false)
    }
  }

  const handleDeleteTaxIdentifier = async (id: number) => {
    setIsDeletingTax(true)
    try {
      const response = await TaxesService.deleteCustomerTaxInfo(id)
      if (response.data.result === 'success') {
        toast({
          title: 'Tax identifier deleted',
          description: 'The tax identifier has been removed successfully',
        })
        
        // Remove the tax identifier from the list
        const updatedTaxInfo = customerTaxInfo.filter(info => info.id !== id)
        setCustomerTaxInfo(updatedTaxInfo)
        if (onTaxInfoChange) {
          onTaxInfoChange(updatedTaxInfo)
        }
      } else {
        toast({
          title: 'Failed to delete tax identifier',
          description: response.data.message || 'An unexpected error occurred',
          variant: 'destructive',
        })
      }
    } catch (error) {
      console.error('Error deleting tax identifier:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete tax identifier',
        variant: 'destructive',
      })
    } finally {
      setIsDeletingTax(false)
    }
  }

  const handleOpenVerifyModal = (taxInfo: CustomerTaxInfo) => {
    setCurrentTaxToVerify(taxInfo)
    setVerificationMethod('automatic')
    setVerificationNotes('')
    setShowVerifyTaxModal(true)
  }

  const handleVerifyTaxIdentifier = async () => {
    if (!currentTaxToVerify) return
    
    setIsVerifyingTax(true)
    try {
      const payload = {
        ...currentTaxToVerify,
        isVerified: true,
        verificationDate: new Date().toISOString(),
        verificationMethod: verificationMethod,
        verificationNotes: verificationNotes
      }
      
      const response = await TaxesService.updateCustomerTaxInfo(currentTaxToVerify.publicId, payload)
      if (response.data.result === 'success') {
        toast({
          title: 'Tax identifier verified',
          description: `The ${currentTaxToVerify.taxType?.name} has been verified successfully`,
        })
        
        // Update the tax identifier in the list
        const updatedTaxInfo = customerTaxInfo.map(info => 
          info.id === currentTaxToVerify.id ? { ...info, isVerified: true, verificationDate: new Date().toISOString() } : info
        )
        setCustomerTaxInfo(updatedTaxInfo)
        if (onTaxInfoChange) {
          onTaxInfoChange(updatedTaxInfo)
        }
        
        setShowVerifyTaxModal(false)
      } else {
        toast({
          title: 'Verification failed',
          description: response.data.message || 'Failed to verify tax identifier',
          variant: 'destructive',
        })
      }
    } catch (error) {
      console.error('Error verifying tax identifier:', error)
      toast({
        title: 'Error',
        description: 'Failed to verify tax identifier',
        variant: 'destructive',
      })
    } finally {
      setIsVerifyingTax(false)
    }
  }

  // Reset form when modal is closed
  useEffect(() => {
    if (!showAddTaxIdentifierModal) {
      setNewTaxIdentifier('')
      setNewTaxIdentifierType(null)
      setNewTaxIdentifierNotes('')
    }
    
    if (!showVerifyTaxModal) {
      setCurrentTaxToVerify(null)
      setVerificationMethod('automatic')
      setVerificationNotes('')
    }
  }, [showAddTaxIdentifierModal, showVerifyTaxModal])

  if (minimal) {
    return (
      <div className={className}>
        {isLoading ? (
          <div className="flex justify-center items-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : customerTaxInfo.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">
            No tax identifiers configured
          </div>
        ) : (
          <div className="space-y-2">
            {customerTaxInfo.map((taxInfo) => (
              <div key={taxInfo.id} className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
                <div>
                  <span className="font-medium text-sm">{taxInfo.taxType?.name}: </span>
                  <span className="text-sm">{taxInfo.taxIdentifier}</span>
                </div>
                <TaxVerificationBadge 
                  status={taxInfo.isVerified ? 'verified' : 'unverified'} 
                  tooltipText={
                    taxInfo.isVerified 
                      ? `Verified on ${new Date(taxInfo.verificationDate || '').toLocaleDateString()}`
                      : 'Not verified yet'
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      <Card className="shadow-sm">
        <CardHeader className="pb-3 flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Tax Identifiers</CardTitle>
            <CardDescription>
              {readonly 
                ? "Customer's tax identification numbers"
                : "Manage customer tax identification numbers"
              }
            </CardDescription>
          </div>
          {!readonly && (
            <Button className="ml-auto" onClick={() => setShowAddTaxIdentifierModal(true)}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Identifier
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : customerTaxInfo.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <FileText className="mx-auto h-12 w-12 mb-4 text-muted-foreground/60" />
              <p className="mb-2">No tax identifiers configured</p>
              <p className="text-sm max-w-md mx-auto">
                {readonly 
                  ? "This customer doesn't have any tax identifiers configured."
                  : "Add customer tax identification numbers such as VAT, TIN, or Sales Tax ID to include them on invoices and ensure proper tax calculation."
                }
              </p>
              {!readonly && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => setShowAddTaxIdentifierModal(true)}
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add Tax Identifier
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Verification Status</TableHead>
                    {!readonly && <TableHead className="w-[120px]">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerTaxInfo.map((taxInfo: CustomerTaxInfo) => (
                    <TableRow key={taxInfo.id}>
                      <TableCell className="font-medium">
                        {taxInfo.taxType?.name}
                      </TableCell>
                      <TableCell>{taxInfo.taxIdentifier}</TableCell>
                      <TableCell>{taxInfo.country}</TableCell>
                      <TableCell>
                        <TaxVerificationBadge 
                          status={taxInfo.isVerified ? 'verified' : 'unverified'} 
                          tooltipText={
                            taxInfo.isVerified 
                              ? `Verified on ${new Date(taxInfo.verificationDate || '').toLocaleDateString()}`
                              : 'Not verified yet'
                          }
                        />
                      </TableCell>
                      {!readonly && (
                        <TableCell>
                          <div className="flex items-center space-x-1">
                            {!taxInfo.isVerified && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => handleOpenVerifyModal(taxInfo)}
                              >
                                <FileCheck className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0"
                              onClick={() => handleDeleteTaxIdentifier(taxInfo.id)}
                              disabled={isDeletingTax}
                            >
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Tax Identifier Modal */}
      <FormModal
        open={showAddTaxIdentifierModal}
        setOpen={setShowAddTaxIdentifierModal}
        title="Add Tax Identifier"
      >
        <div className="space-y-4 py-2 pb-4">
          <div className="space-y-2">
            <Label htmlFor="tax-type">Tax Type</Label>
            <TaxTypeSelector
              placeholder="Select tax type"
              onSelect={(value) => setNewTaxIdentifierType(value)}
              value={newTaxIdentifierType?.id}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="tax-identifier">Tax Identifier</Label>
            <Input
              id="tax-identifier"
              placeholder="Enter tax identifier number"
              value={newTaxIdentifier}
              onChange={(e) => setNewTaxIdentifier(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Select 
              value={newTaxIdentifierCountry} 
              onValueChange={setNewTaxIdentifierCountry}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Additional information about this tax identifier"
              value={newTaxIdentifierNotes}
              onChange={(e) => setNewTaxIdentifierNotes(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowAddTaxIdentifierModal(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddTaxIdentifier}
              disabled={isSavingIdentifier}
            >
              {isSavingIdentifier ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Identifier'
              )}
            </Button>
          </div>
        </div>
      </FormModal>

      {/* Verify Tax Modal */}
      <FormModal
        open={showVerifyTaxModal}
        setOpen={setShowVerifyTaxModal}
        title="Verify Tax Identifier"
      >
        <div className="space-y-4 py-2 pb-4">
          <div>
            <p className="text-sm font-medium mb-2">Tax Identifier</p>
            <div className="bg-muted p-3 rounded-md">
              <p className="font-medium">{currentTaxToVerify?.taxType?.name}: {currentTaxToVerify?.taxIdentifier}</p>
              <p className="text-sm text-muted-foreground mt-1">{currentTaxToVerify?.country}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-base">Verification Method</Label>
            <RadioGroup
              value={verificationMethod}
              onValueChange={(value) => setVerificationMethod(value as 'automatic' | 'manual')}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="automatic" id="automatic" />
                <Label htmlFor="automatic" className="font-normal">
                  Automatic Verification
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="manual" id="manual" />
                <Label htmlFor="manual" className="font-normal">
                  Manual Verification
                </Label>
              </div>
            </RadioGroup>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="verification-notes">Verification Notes (Optional)</Label>
            <Textarea
              id="verification-notes"
              placeholder="Optional notes about the verification process"
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowVerifyTaxModal(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleVerifyTaxIdentifier}
              disabled={isVerifyingTax}
            >
              {isVerifyingTax ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Identifier'
              )}
            </Button>
          </div>
        </div>
      </FormModal>
    </div>
  )
} 